<template>
  <component
    :is="PopupMapComponent"
    v-if="PopupMapComponent"
    ref="popupMapRef"
  />
</template>

<script lang="ts" setup>
import {
  nextTick, onBeforeUnmount, onMounted, ref, shallowRef, type Component,
} from 'vue'
import useIsMobile from 'src/composables/useIsMobile'
import useContextStore from 'src/store/useContextStore'
import {
  registerPopupMapOpen,
  unregisterPopupMapOpen,
  type PopupMapLpuItem,
} from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/openPopupMap'

type PopupMapWindow = Window & {
  YANDEX_MAPS_URL?: string
  COUNTRY_DATA?: {
    iso?: string
  }
}

type PopupMapInstance = {
  handleOpenDialog: (_event: { detail: { lpusData: PopupMapLpuItem[] } }) => void
}

const { context } = useContextStore()
const isMobile = useIsMobile()
const PopupMapComponent = shallowRef<Component>()
const popupMapRef = ref<PopupMapInstance>()

function openFromHost(lpusData: PopupMapLpuItem[]) {
  popupMapRef.value?.handleOpenDialog({ detail: { lpusData } })
}

onMounted(async () => {
  const popupMapWindow = window as PopupMapWindow

  popupMapWindow.MOBILE_VERSION = isMobile.value

  /**
   * На `/ssr-vue-page` нет django `js_window.html`.
   * Без LANGUAGE_DATA импорт PopupMap падает: `www/utils` → getAxiosInstance читает currentLanguage.
   */
  if (!popupMapWindow.LANGUAGE_DATA?.currentLanguage) {
    const currentLanguage = context.current_language

    popupMapWindow.LANGUAGE_DATA = {
      ...popupMapWindow.LANGUAGE_DATA,
      needTranslate: Boolean(context.need_translate),
      currentLanguage: {
        name: currentLanguage?.name,
        iso: currentLanguage?.iso || 'ru',
        flagImage: currentLanguage?.flag_image,
      },
      availableLanguages: context.available_languages || [],
      defaultLanguageIso: context.default_language_iso || 'ru',
    }
  }

  if (!popupMapWindow.COUNTRY_DATA?.iso) {
    popupMapWindow.COUNTRY_DATA = {
      ...popupMapWindow.COUNTRY_DATA,
      iso: context.request.country.iso,
    }
  }

  if (!popupMapWindow.YANDEX_MAPS_URL) {
    const apiKey = context.yandex_maps_api_key
    const suggestApiKey = context.yandex_maps_suggest_api_key
    let yandexMapsUrl = '//api-maps.yandex.ru/2.1/?lang=ru-RU'

    if (typeof apiKey === 'string' && apiKey) {
      yandexMapsUrl += `&apikey=${apiKey}`
    }

    if (typeof suggestApiKey === 'string' && suggestApiKey) {
      yandexMapsUrl += `&suggest_apikey=${suggestApiKey}`
    }

    popupMapWindow.YANDEX_MAPS_URL = yandexMapsUrl
  }

  // @ts-ignore
  await import('www/library.blocks/common/b-button/b-button.scss')
  // @ts-ignore
  await import('www/vue_components/common/PopupMap/index.scss')

  const module = isMobile.value
    ? await import('www/vue_components/common/PopupMap/components/mobile/PopupMapMobile.vue')
    : await import('www/vue_components/common/PopupMap/components/desktop/PopupMapDesktop.vue')

  PopupMapComponent.value = module.default
  await nextTick()
  registerPopupMapOpen(openFromHost)
})

onBeforeUnmount(() => {
  unregisterPopupMapOpen(openFromHost)
})
</script>
